<template>
  <div class="genre-picker">
    <!-- Main Genres -->
    <div class="genre-section">
      <label class="field-label">
        Main Genre(s) <span class="required-mark">*</span>
      </label>
      <div v-if="modelValue.main.length > 0" class="selected-genres">
        <span
          v-for="slug in modelValue.main"
          :key="slug"
          class="genre-chip genre-chip--main"
        >
          {{ labelFor(slug, 'main') }}
          <button type="button" class="chip-remove" @click="removeMain(slug)">×</button>
        </span>
      </div>
      <select
        v-if="canAddMore && availableMainGenres.length > 0"
        class="genre-select"
        @change="addMain($event.target.value); $event.target.value = ''"
      >
        <option value="" disabled selected>Add a main genre...</option>
        <option v-for="g in availableMainGenres" :key="g.slug" :value="g.slug">
          {{ g.label }}
        </option>
      </select>
    </div>

    <!-- Subgenres -->
    <div class="genre-section">
      <label class="field-label">
        Subgenre(s) <span class="optional-mark">(optional)</span>
      </label>
      <div v-if="modelValue.sub.length > 0" class="selected-genres">
        <span
          v-for="slug in modelValue.sub"
          :key="slug"
          class="genre-chip genre-chip--sub"
        >
          {{ labelFor(slug, 'sub') }}
          <button type="button" class="chip-remove" @click="removeSub(slug)">×</button>
        </span>
      </div>
      <select
        v-if="canAddMore && availableSubGenres.length > 0"
        class="genre-select"
        @change="addSub($event.target.value); $event.target.value = ''"
      >
        <option value="" disabled selected>Add a subgenre...</option>
        <option v-for="g in availableSubGenres" :key="g.slug" :value="g.slug">
          {{ g.label }}
        </option>
      </select>
    </div>

    <p v-if="totalCount >= 3" class="max-reached">Max 3 genres — remove one to add another</p>
    <p v-if="error" class="field-error">{{ error }}</p>
  </div>
</template>

<script>
import apiService from '../../apiService';

export default {
  name: 'GenrePicker',
  props: {
    modelValue: {
      type: Object,
      default: () => ({ main: [], sub: [] }),
    },
    error: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  data() {
    return { allMainGenres: [], allSubGenres: [] };
  },
  computed: {
    totalCount() { return this.modelValue.main.length + this.modelValue.sub.length; },
    canAddMore()  { return this.totalCount < 3; },
    availableMainGenres() {
      return this.allMainGenres.filter(g => !this.modelValue.main.includes(g.slug));
    },
    availableSubGenres() {
      const selected = this.modelValue.main;
      const pool = selected.length > 0
        ? this.allSubGenres.filter(g => selected.includes(g.parent_slug))
        : this.allSubGenres;
      return pool.filter(g => !this.modelValue.sub.includes(g.slug));
    },
  },
  async created() {
    try {
      const genres = await apiService.getGenres();
      this.allMainGenres = genres.mainGenres || [];
      this.allSubGenres  = genres.subGenres  || [];
    } catch (e) {
      console.error('Failed to load genres:', e);
    }
  },
  methods: {
    labelFor(slug, type) {
      const list = type === 'main' ? this.allMainGenres : this.allSubGenres;
      return list.find(g => g.slug === slug)?.label ?? slug;
    },
    addMain(slug) {
      if (!slug || this.modelValue.main.includes(slug) || !this.canAddMore) return;
      this.$emit('update:modelValue', { ...this.modelValue, main: [...this.modelValue.main, slug] });
    },
    removeMain(slug) {
      this.$emit('update:modelValue', { ...this.modelValue, main: this.modelValue.main.filter(s => s !== slug) });
    },
    addSub(slug) {
      if (!slug || this.modelValue.sub.includes(slug) || !this.canAddMore) return;
      this.$emit('update:modelValue', { ...this.modelValue, sub: [...this.modelValue.sub, slug] });
    },
    removeSub(slug) {
      this.$emit('update:modelValue', { ...this.modelValue, sub: this.modelValue.sub.filter(s => s !== slug) });
    },
  },
};
</script>

<style scoped>
.genre-picker   { display: flex; flex-direction: column; gap: 1rem; }
.genre-section  { display: flex; flex-direction: column; gap: 0.5rem; }
.field-label    { font-size: 0.875rem; font-weight: 500; color: #a0a0a0; }
.required-mark  { color: #f87171; margin-left: 0.2rem; }
.optional-mark  { color: #6b7280; font-weight: 400; font-size: 0.8rem; margin-left: 0.25rem; }
.selected-genres { display: flex; flex-wrap: wrap; gap: 0.4rem; }

.genre-chip {
  display: inline-flex; align-items: center; gap: 0.25rem;
  padding: 0.25rem 0.6rem; border-radius: 999px; font-size: 0.8rem;
}
.genre-chip--main {
  background: rgba(167,139,250,0.2); border: 1px solid rgba(167,139,250,0.4); color: #ddd6fe;
}
.genre-chip--sub {
  background: rgba(94,234,212,0.15); border: 1px solid rgba(94,234,212,0.3); color: #99f6e4;
}

.chip-remove {
  background: none; border: none; color: inherit; cursor: pointer;
  font-size: 0.9rem; padding: 0; line-height: 1; opacity: 0.7;
}
.chip-remove:hover { color: #f87171; opacity: 1; }

.genre-select {
  padding: 0.5rem 0.75rem; background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.15); border-radius: 6px;
  color: #f3f3f3; font-size: 0.875rem; cursor: pointer;
}
.genre-select:focus { outline: none; border-color: rgba(167,139,250,0.5); }
.max-reached { font-size: 0.8rem; color: #6b7280; margin: 0; }
.field-error  { font-size: 0.8rem; color: #f87171; margin: 0; }
</style>
